<template>
  <div class="analytic-plan-rules-manager">
    <v-card>
      <v-card-title>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar"
          single-line
          hide-details
          class="mb-2"
        ></v-text-field>
        <v-spacer></v-spacer>
        <div align="right">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="refreshList"
                color="primary"
                icon
                v-bind="attrs"
                v-on="on"
                :loading="loading"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Actualizar</span>
          </v-tooltip>
          <v-tooltip top v-if="!hideCreateButton">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="openCreateDialog()"
                color="primary"
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>Nueva Referencia de Plan Analítico</span>
          </v-tooltip>
          <v-tooltip top v-if="!hideCreateButton && selectedReferences.length > 0">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="openDeleteDialog()"
                color="primary"
                v-bind="attrs"
                v-on="on"
                icon
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>Eliminar Referencia</span>
          </v-tooltip>
        </div>
      </v-card-title>
      <v-data-table
        ref="analyticPlansByReferenceTableV2"
        :headers="headers"
        :items="references"
        :search="search"
        :loading="loading"
        show-select
        v-model="selectedReferences"
        item-key="id"
        class="elevation-1"
        :footer-props="{
          'items-per-page-text': itemsPerPageText
        }"
      >
        <!-- ID Column -->
      <template v-slot:item.id="{ item }">
        <span class="font-weight-bold primary--text">{{ item.id }}</span>
      </template>

      <!-- Reference Type Column -->
      <template v-slot:item.account_rule_type="{ item }">
        <v-chip x-small color="primary" outlined>
          {{ getReferenceTypeLabel(item.account_rule_type) }}
        </v-chip>
      </template>

      <!-- Account Rules Column -->
      <template v-slot:item.account_rules="{ item }">
        <v-chip-group column>
          <v-chip
            v-for="(rule, index) in item.account_rules"
            :key="index"
            x-small
            outlined
            color="primary"
          >
            <span v-if="typeof rule === 'object' && rule.description">
              {{ rule.account }} - {{ rule.description }}
            </span>
            <span v-else>
              {{ typeof rule === 'object' ? rule.account : rule }}
            </span>
          </v-chip>
        </v-chip-group>
      </template>

      <!-- Analytic Plans Column -->
      <template v-slot:item.analytic_plans="{ item }">
        <v-chip-group column>
          <v-chip
            v-for="(plan, index) in item.analytic_plans"
            :key="index"
            x-small
            color="primary"
            outlined
          >
            {{ plan.name }}
          </v-chip>
        </v-chip-group>
      </template>

      <!-- Thirds Column -->
      <template v-slot:item.thirds="{ item }">
        <span v-if="!item.thirds || item.thirds.length === 0" class="grey--text font-italic">Todos los terceros</span>
        <v-chip-group v-else column>
          <v-chip
            v-for="(third, index) in item.thirds"
            :key="index"
            x-small
            outlined
            color="primary"
          >
            {{ third.name }}
          </v-chip>
        </v-chip-group>
      </template>

      <!-- References Column -->
      <template v-slot:item.references="{ item }">
        <span v-if="!item.references || item.references.length === 0" class="grey--text font-italic">Sin referencias</span>
        <v-chip-group v-else column>
          <v-chip
            v-for="(reference, index) in item.references"
            :key="index"
            x-small
            outlined
            color="primary"
          >
            {{ reference }}
          </v-chip>
        </v-chip-group>
      </template>

      <!-- Actions Column -->
      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              class="mr-2"
              v-bind="attrs"
              v-on="on"
              @click="editReference(item)"
            >
              mdi-pencil
            </v-icon>
          </template>
          <span>Editar</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              v-bind="attrs"
              v-on="on"
              @click="deleteReference(item)"
            >
              mdi-delete
            </v-icon>
          </template>
          <span>Eliminar</span>
        </v-tooltip>
      </template>
    </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center sticky-header">
          <span class="text-h5">{{ dialogTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeDialogWithCheck" class="mr-2">Cerrar</v-btn>
          <v-btn
            color="primary"
            :outlined="!hasUnsavedChanges"
            @click="saveReference"
            :loading="saving"
            :disabled="saving"
          >
            Guardar
          </v-btn>
        </v-card-title>
        <v-card-text class="scrollable-content">
          <v-container>
            <v-form ref="referenceForm">
              <!-- Apply to Suppliers/Customers Checkboxes -->
              <v-row dense>
                <v-col cols="6">
                  <v-checkbox
                    v-model="editedItem.apply_to_suppliers"
                    label="Aplicar a Proveedores"
                    dense
                  ></v-checkbox>
                </v-col>
                <v-col cols="6">
                  <v-checkbox
                    v-model="editedItem.apply_to_customers"
                    label="Aplicar a Clientes"
                    dense
                  ></v-checkbox>
                </v-col>
              </v-row>

              <!-- Suppliers and Customers Selectors -->
              <v-row dense>
                <!-- Empty spacer for suppliers when unchecked -->
                <v-col cols="6" v-if="!editedItem.apply_to_suppliers"></v-col>

                <!-- Suppliers Selector -->
                <v-col cols="6" v-if="editedItem.apply_to_suppliers">
                  <div>
                    <label class="v-label theme--light" style="font-size: 12px;">
                      Proveedores (Opcional)
                    </label>
                    <div class="mt-2">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        @click="openSupplierSelectorDialog"
                      >
                        <v-icon left small>mdi-account-multiple</v-icon>
                        Seleccionar Proveedores
                        <v-badge
                          v-if="selectedSuppliers.length > 0"
                          :content="selectedSuppliers.length"
                          color="primary"
                          inline
                          class="ml-2"
                        />
                      </v-btn>
                    </div>
                    <div v-if="selectedSuppliers.length > 0" class="mt-2">
                      <v-chip
                        v-for="supplier in selectedSuppliers"
                        :key="supplier.id"
                        small
                        close
                        class="mr-1 mb-1"
                        @click:close="removeSupplier(supplier)"
                      >
                        {{ supplier.account }} - {{ supplier.name }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>

                <!-- Customers Selector -->
                <v-col cols="6" v-if="editedItem.apply_to_customers">
                  <div>
                    <label class="v-label theme--light" style="font-size: 12px;">
                      Clientes (Opcional)
                    </label>
                    <div class="mt-2">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        @click="openCustomerSelectorDialog"
                      >
                        <v-icon left small>mdi-account-multiple</v-icon>
                        Seleccionar Clientes
                        <v-badge
                          v-if="selectedCustomers.length > 0"
                          :content="selectedCustomers.length"
                          color="primary"
                          inline
                          class="ml-2"
                        />
                      </v-btn>
                    </div>
                    <div v-if="selectedCustomers.length > 0" class="mt-2">
                      <v-chip
                        v-for="customer in selectedCustomers"
                        :key="customer.id"
                        small
                        close
                        class="mr-1 mb-1"
                        @click:close="removeCustomer(customer)"
                      >
                        {{ customer.account }} - {{ customer.name }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Tipo de Regla and Account Rules on same row -->
              <v-row dense class="mt-3">
                <!-- Reference Type Selection -->
                <v-col cols="4">
                  <v-radio-group
                    v-model="editedItem.account_rule_type"
                    column
                    label="Tipo de Regla"
                    :rules="[validationRules.required]"
                  >
                    <v-radio
                      label="Por Cuentas (exactas)"
                      value="by_accounts"
                    ></v-radio>
                    <v-radio
                      label="Por Grupo de Cuentas (prefijo)"
                      value="by_account_group"
                    ></v-radio>
                  </v-radio-group>
                </v-col>

                <!-- Account Rules Input -->
                <v-col cols="8">
                  <v-combobox
                    v-model="editedItem.account_rules"
                    label="Reglas de Cuentas"
                    :items="[]"
                    item-text="account"
                    return-object
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    :hint="editedItem.account_rule_type === 'by_account_group'
                      ? 'Prefijos de cuentas (ej: 43, 57). Puedes escribir múltiples. Doble click o click derecho para editar.'
                      : 'Usa el botón Seleccionar cuentas para elegir cuentas o escribe manualmente.'"
                    persistent-hint
                    :rules="[validationRules.required]"
                    :filter="filterAccountBy"
                    @input.native="onAccountNativeInput"
                    @change="normalizeAccountRulesInput"
                    @blur="resetAccountItems"
                  >
                    <template slot="item" slot-scope="data">
                      {{ data.item.account }} - {{ data.item.description }}
                    </template>
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        small
                        close
                        @click:close="removeAccountRule(item)"
                        @contextmenu.prevent="editedItem.account_rule_type === 'by_account_group' ? openEditAccountRuleDialog(item, index) : null"
                        @dblclick="editedItem.account_rule_type === 'by_account_group' ? openEditAccountRuleDialog(item, index) : null"
                      >
                        <!-- For group rules: only show prefix, no description -->
                        <span v-if="editedItem.account_rule_type === 'by_account_group'">
                          {{ typeof item === 'object' ? item.account : item }}
                        </span>
                        <!-- For exact account rules: show description if available -->
                        <span v-else-if="typeof item === 'object' && item.description && item.description.trim()">
                          {{ item.account }} - {{ item.description }}
                        </span>
                        <span v-else>
                          {{ typeof item === 'object' ? item.account : item }}
                        </span>
                      </v-chip>
                    </template>
                  </v-combobox>

                  <!-- Edit Account Rule Dialog -->
                  <v-dialog v-model="editAccountRuleDialog" max-width="500px">
                    <v-card>
                      <v-card-title>
                        <span class="text-h5">Editar Prefijo de Cuenta</span>
                      </v-card-title>
                      <v-card-text>
                        <v-text-field
                          v-model="editingAccountRuleText"
                          label="Prefijo de Cuenta"
                          outlined
                          autofocus
                          @keyup.enter="saveEditedAccountRule"
                        ></v-text-field>
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey" text @click="editAccountRuleDialog = false">Cancelar</v-btn>
                        <v-btn color="primary" @click="saveEditedAccountRule">Guardar</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <!-- Buttons for loading accounts -->
                  <div v-if="editedItem.account_rule_type === 'by_accounts'" class="mt-2">
                    <v-btn
                      @click="openThirdsAccountsDialog"
                      :disabled="selectedSuppliers.length === 0 && selectedCustomers.length === 0"
                      color="primary"
                      outlined
                      small
                      class="mr-2"
                    >
                      <v-icon left small>mdi-account-multiple</v-icon>
                      Cargar cuentas de terceros
                    </v-btn>
                    <v-btn
                      @click="openAccountSelectorDialog"
                      color="primary"
                      outlined
                      small
                    >
                      Seleccionar cuentas
                    </v-btn>
                  </div>
                </v-col>
              </v-row>

              <!-- References/Tags Input -->
              <v-combobox
                v-model="editedItem.references"
                label="Referencias/Etiquetas"
                multiple
                chips
                deletable-chips
                small-chips
                hint="Etiquetas para categorización (ej: 'compras', 'ventas', 'vip'). No diferencia mayúsculas ni acentos. Doble click o click derecho para editar."
                persistent-hint
                :rules="[validationRules.required]"
                class="mt-3"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    small
                    close
                    @click:close="editedItem.references.splice(index, 1)"
                    @contextmenu.prevent="openEditReferenceDialog(item, index)"
                    @dblclick="openEditReferenceDialog(item, index)"
                  >
                    {{ item }}
                  </v-chip>
                </template>
              </v-combobox>

              <!-- Edit Reference Dialog -->
              <v-dialog v-model="editReferenceDialog" max-width="500px">
                <v-card>
                  <v-card-title>
                    <span class="text-h5">Editar Referencia</span>
                  </v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model="editingReferenceText"
                      label="Referencia"
                      outlined
                      autofocus
                      @keyup.enter="saveEditedReference"
                    ></v-text-field>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="editReferenceDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="saveEditedReference">Guardar</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- Analytic Plans Selector -->
              <div class="mt-5">
                <v-label>Planes Analíticos</v-label>
                <v-btn
                  block
                  outlined
                  color="primary"
                  class="mt-2"
                  @click="openAnalyticPlansDialog"
                >
                  <v-icon left>mdi-chart-tree</v-icon>
                  {{ getAnalyticPlansButtonText }}
                </v-btn>
                <div v-if="editedItem.analytic_plans.length > 0" class="mt-2">
                  <v-chip
                    v-for="(plan, idx) in editedItem.analytic_plans"
                    :key="idx"
                    small
                    color="blue"
                    dark
                    close
                    class="mr-1 mb-1"
                    @click:close="removeAnalyticPlan(idx)"
                  >
                    {{ plan.name }} ({{ getSelectedLevelsCount(plan) }} niveles)
                  </v-chip>
                </div>
                <div class="text-caption grey--text mt-1">
                  Selecciona los planes analíticos y sus niveles específicos
                </div>
                <v-alert
                  v-if="analyticPlansError"
                  type="error"
                  dense
                  class="mt-2"
                  text
                >
                  No se ha seleccionado ningún plan analítico. Debe seleccionar al menos uno.
                </v-alert>

                <!-- Tree structure showing selected plans and levels -->
                <div v-if="hasAnySelectedLevels" class="mt-3 selected-levels-tree">
                  <div v-for="(plan, planIdx) in editedItem.analytic_plans" :key="planIdx" class="plan-tree-item mb-2">
                    <template v-if="getPlanSelectedLevelsTree(plan).length > 0">
                      <div class="plan-name">
                        <v-icon small color="blue darken-2" class="mr-1">mdi-folder-outline</v-icon>
                        <span class="font-weight-medium">{{ plan.name }}</span>
                      </div>
                      <div class="ml-4 mt-1">
                        <div v-for="(treeNode, nodeIdx) in getPlanSelectedLevelsTree(plan)" :key="nodeIdx">
                          <!-- Level 1 with selected Level 2 children -->
                          <div v-if="treeNode.children.length > 0">
                            <div class="level1-parent grey--text text--darken-1">
                              <v-icon x-small class="mr-1">mdi-chevron-right</v-icon>
                              {{ treeNode.code }} - {{ treeNode.name }}
                            </div>
                            <div class="ml-4">
                              <div
                                v-for="(child, childIdx) in treeNode.children"
                                :key="childIdx"
                                class="level2-child text-body-2"
                              >
                                <v-icon x-small color="green" class="mr-1">mdi-checkbox-marked-circle</v-icon>
                                {{ child.code }} - {{ child.name }}
                              </div>
                            </div>
                          </div>
                          <!-- Standalone Level 1 (selected, no children) -->
                          <div v-else-if="treeNode.isSelected" class="level1-standalone text-body-2">
                            <v-icon x-small color="green" class="mr-1">mdi-checkbox-marked-circle</v-icon>
                            {{ treeNode.code }} - {{ treeNode.name }}
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </v-form>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">¿Eliminar referencia(s)?</v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar {{ selectedReferences.length }} referencia(s)? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unsaved Changes Confirmation Dialog -->
    <v-dialog v-model="unsavedChangesDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">¿Cerrar sin guardar?</v-card-title>
        <v-card-text>
          Tienes cambios sin guardar. ¿Estás seguro de que deseas cerrar sin guardar estos cambios?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="unsavedChangesDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmCloseWithoutSaving">Cerrar sin guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Analytic Plans Selection Dialog -->
    <v-dialog v-model="analyticPlansDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Seleccionar Planes Analíticos</span>
        </v-card-title>
        <v-card-text>
          <div v-if="analyticPlansOptions.length === 0" class="text-center py-4">
            No hay planes analíticos disponibles
          </div>
          <div v-else>
            <v-list>
              <v-list-item
                v-for="plan in analyticPlansOptions"
                :key="plan.code"
                @click="togglePlanSelection(plan)"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="isPlanSelected(plan)"
                    readonly
                    color="primary"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ plan.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ plan.code }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="isPlanSelected(plan)">
                  <v-btn
                    small
                    text
                    color="primary"
                    @click.stop="openLevelSelector(plan)"
                  >
                    <v-icon left small>mdi-tune</v-icon>
                    Configurar niveles
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeAnalyticPlansDialog">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Thirds Accounts Selection Dialog -->
    <!-- Thirds Accounts Selector Dialog -->
    <v-dialog v-model="thirdsAccountsDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Cuentas de Terceros Seleccionados</span>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelThirdsAccountsSelector" class="mr-2">Cancelar</v-btn>
          <v-btn color="primary" @click="applyThirdsAccounts">Aplicar</v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="auto">
              <v-text-field
                v-model="thirdsAccountsSearch"
                append-icon="mdi-magnify"
                label="Buscar"
                single-line
                hide-details
                dense
                outlined
                style="width: 280px;"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <!-- Available Accounts (Left Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Cuentas Disponibles</div>
              <v-data-table
                v-model="availableSelectedThirdsAccounts"
                :headers="thirdsAccountsHeaders"
                :items="availableThirdsAccounts"
                :search="thirdsAccountsSearch"
                show-select
                item-key="code"
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                height="400"
                fixed-header
                class="elevation-1"
                @dblclick:row="onAvailableThirdsAccountDoubleClick"
              >
              </v-data-table>
            </v-col>

            <!-- Transfer Buttons (Center) -->
            <v-col cols="2" class="d-flex flex-column align-center justify-center">
              <v-btn
                color="primary"
                @click="addThirdsAccountsToSelected"
                :disabled="availableSelectedThirdsAccounts.length === 0"
                class="mb-3"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                @click="removeThirdsAccountsFromSelected"
                :disabled="selectedSelectedThirdsAccounts.length === 0"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
            </v-col>

            <!-- Selected Accounts (Right Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Cuentas Seleccionadas</div>
              <v-data-table
                v-model="selectedSelectedThirdsAccounts"
                :headers="thirdsAccountsHeaders"
                :items="tempSelectedThirdsAccounts"
                show-select
                item-key="code"
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                height="400"
                fixed-header
                class="elevation-1"
                @dblclick:row="onSelectedThirdsAccountDoubleClick"
              >
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Supplier Selector Dialog -->
    <v-dialog v-model="supplierSelectorDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Seleccionar Proveedores</span>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelSupplierSelector" class="mr-2">Cancelar</v-btn>
          <v-btn color="primary" @click="applySupplierSelector">Aplicar</v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="auto">
              <v-text-field
                v-model="supplierSelectorSearch"
                append-icon="mdi-magnify"
                label="Buscar"
                single-line
                hide-details
                dense
                outlined
                clearable
                style="width: 280px;"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <!-- Available Suppliers (Left Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Proveedores Disponibles</div>
              <v-data-table
                v-model="availableSelectedSuppliers"
                :headers="supplierHeaders"
                :items="availableSuppliers"
                show-select
                dense
                :items-per-page="50"
                :loading="supplierLoading"
                :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                height="400"
                fixed-header
                class="elevation-1"
                @dblclick:row="onAvailableSupplierDoubleClick"
              >
                <template v-slot:item.expens_account="{ item }">
                  {{ item.expens_account || '-' }}
                </template>
              </v-data-table>
            </v-col>

            <!-- Transfer Buttons (Center) -->
            <v-col cols="2" class="d-flex flex-column align-center justify-center">
              <v-btn
                color="primary"
                @click="addSuppliersToSelected"
                :disabled="availableSelectedSuppliers.length === 0"
                class="mb-3"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                @click="removeSuppliersFromSelected"
                :disabled="selectedSelectedSuppliers.length === 0"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
            </v-col>

            <!-- Selected Suppliers (Right Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Proveedores Seleccionados</div>
              <v-data-table
                v-model="selectedSelectedSuppliers"
                :headers="supplierHeaders"
                :items="tempSelectedSuppliers"
                show-select
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                height="400"
                fixed-header
                class="elevation-1"
                @dblclick:row="onSelectedSupplierDoubleClick"
              >
                <template v-slot:item.expens_account="{ item }">
                  {{ item.expens_account || '-' }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Customer Selector Dialog -->
    <v-dialog v-model="customerSelectorDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Seleccionar Clientes</span>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelCustomerSelector" class="mr-2">Cancelar</v-btn>
          <v-btn color="primary" @click="applyCustomerSelector">Aplicar</v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="auto">
              <v-text-field
                v-model="customerSelectorSearch"
                append-icon="mdi-magnify"
                label="Buscar"
                single-line
                hide-details
                dense
                outlined
                clearable
                style="width: 280px;"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <!-- Available Customers (Left Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Clientes Disponibles</div>
              <v-data-table
                v-model="availableSelectedCustomers"
                :headers="customerHeaders"
                :items="availableCustomers"
                show-select
                dense
                :items-per-page="50"
                :loading="customerLoading"
                :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                height="400"
                fixed-header
                class="elevation-1"
                @dblclick:row="onAvailableCustomerDoubleClick"
              >
                <template v-slot:item.income_account="{ item }">
                  {{ item.income_account || '-' }}
                </template>
              </v-data-table>
            </v-col>

            <!-- Transfer Buttons (Center) -->
            <v-col cols="2" class="d-flex flex-column align-center justify-center">
              <v-btn
                color="primary"
                @click="addCustomersToSelected"
                :disabled="availableSelectedCustomers.length === 0"
                class="mb-3"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                @click="removeCustomersFromSelected"
                :disabled="selectedSelectedCustomers.length === 0"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
            </v-col>

            <!-- Selected Customers (Right Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Clientes Seleccionados</div>
              <v-data-table
                v-model="selectedSelectedCustomers"
                :headers="customerHeaders"
                :items="tempSelectedCustomers"
                show-select
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                height="400"
                fixed-header
                class="elevation-1"
                @dblclick:row="onSelectedCustomerDoubleClick"
              >
                <template v-slot:item.income_account="{ item }">
                  {{ item.income_account || '-' }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Account Selector Dialog -->
    <v-dialog v-model="accountSelectorDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Seleccionar Cuentas</span>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelAccountSelector" class="mr-2">Cancelar</v-btn>
          <v-btn color="primary" @click="applyAccountSelector">Aplicar</v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="auto">
              <v-text-field
                v-model="accountSelectorSearch"
                append-icon="mdi-magnify"
                label="Buscar"
                single-line
                hide-details
                dense
                outlined
                clearable
                style="width: 280px;"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <!-- Available Accounts (Left Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Cuentas Disponibles</div>
              <v-data-table
                v-model="availableSelectedAccounts"
                :headers="accountSelectorHeaders"
                :items="availableAccounts"
                item-key="account"
                show-select
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                height="400"
                fixed-header
                class="elevation-1"
                :loading="accountSelectorLoading"
                @dblclick:row="onAvailableAccountDoubleClick"
              >
                <template v-slot:item.description="{ item }">
                  {{ item.description || '-' }}
                </template>
              </v-data-table>
            </v-col>

            <!-- Transfer Buttons (Center) -->
            <v-col cols="2" class="d-flex flex-column align-center justify-center">
              <v-btn
                color="primary"
                @click="addAccountsToSelected"
                :disabled="availableSelectedAccounts.length === 0"
                class="mb-3"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                @click="removeAccountsFromSelected"
                :disabled="selectedSelectedAccounts.length === 0"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
            </v-col>

            <!-- Selected Accounts (Right Table) -->
            <v-col cols="5">
              <div class="text-subtitle-2 mb-2">Cuentas Seleccionadas</div>
              <v-data-table
                v-model="selectedSelectedAccounts"
                :headers="accountSelectorHeaders"
                :items="tempSelectedAccounts"
                item-key="account"
                show-select
                dense
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
                height="400"
                fixed-header
                class="elevation-1"
                @dblclick:row="onSelectedAccountDoubleClick"
              >
                <template v-slot:item.description="{ item }">
                  {{ item.description || '-' }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Level Selector Popup -->
    <AnalyticLevelSelectorPopup
      v-if="currentPlanForLevelSelection"
      v-model="levelSelectorDialog"
      :plan="currentPlanForLevelSelection"
      @apply="onLevelSelectionApply"
    />
  </div>
</template>

<script>
import { ApiCompanyRepository } from './ApiCompanyRepository';
import { SearchThirdsQuery } from '../application/SearchThirdsQuery';
import { ApiThirdRepository } from './ApiThirdRepository';
import { client } from '@/api/client';
import AnalyticLevelSelectorPopup from './AnalyticLevelSelectorPopup.vue';

export default {
  name: 'AnalyticPlanRulesManagerV2',

  components: {
    AnalyticLevelSelectorPopup
  },

  props: {
    companydbId: {
      type: Number,
      required: true
    },
    userId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      search: '',
      loading: false,
      saving: false,
      references: [],
      selectedReferences: [],
      dialog: false,
      deleteDialog: false,
      unsavedChangesDialog: false,
      editedIndex: -1,
      editedItem: {
        id: null,
        account_rule_type: 'by_accounts',
        account_rules: [],
        analytic_plans: [],
        thirds: [],
        references: []
      },
      originalEditedItem: null,
      hasUnsavedChanges: false,
      selectedPlanCodes: [],
      hideCreateButton: false,
      companyRepository: new ApiCompanyRepository(),
      searchThirdsQuery: new SearchThirdsQuery(new ApiThirdRepository()),
      analyticPlans: null,
      accountOptions: [],
      accountSearchDebounce: null,
      // Suppliers
      selectedSuppliers: [],
      supplierOptions: [],
      supplierLoading: false,
      supplierSearchDebounce: null,
      supplierAbortController: null,
      supplierLastSearch: '',
      // Customers
      selectedCustomers: [],
      customerOptions: [],
      customerLoading: false,
      customerSearchDebounce: null,
      customerAbortController: null,
      customerLastSearch: '',
      analyticPlansDialog: false,
      levelSelectorDialog: false,
      currentPlanForLevelSelection: null,
      analyticPlansError: false,
      editReferenceDialog: false,
      editingReferenceIndex: null,
      editingReferenceText: '',
      editAccountRuleDialog: false,
      editingAccountRuleIndex: null,
      editingAccountRuleText: '',
      thirdsAccountsDialog: false,
      thirdsAccountsSearch: '',
      allThirdsAccounts: [],
      tempSelectedThirdsAccounts: [],
      availableSelectedThirdsAccounts: [],
      selectedSelectedThirdsAccounts: [],
      thirdsAccountsHeaders: [
        { text: 'Cuenta', value: 'code', sortable: true },
        { text: 'Descripción', value: 'description', sortable: true }
      ],
      supplierSelectorDialog: false,
      supplierSelectorSearch: '',
      allSuppliers: [],
      supplierTotalCount: 0,
      supplierPage: 1,
      supplierItemsPerPage: 50,
      supplierLoading: false,
      tempSelectedSuppliers: [],
      availableSelectedSuppliers: [],
      selectedSelectedSuppliers: [],
      supplierHeaders: [
        { text: 'Cuenta', value: 'account', sortable: true },
        { text: 'Nombre', value: 'name', sortable: true },
        { text: 'Cuenta Gasto', value: 'expens_account', sortable: true }
      ],
      customerSelectorDialog: false,
      customerSelectorSearch: '',
      allCustomers: [],
      customerTotalCount: 0,
      customerPage: 1,
      customerItemsPerPage: 50,
      customerLoading: false,
      tempSelectedCustomers: [],
      availableSelectedCustomers: [],
      selectedSelectedCustomers: [],
      customerHeaders: [
        { text: 'Cuenta', value: 'account', sortable: true },
        { text: 'Nombre', value: 'name', sortable: true },
        { text: 'Cuenta Ingreso', value: 'income_account', sortable: true }
      ],
      accountSelectorDialog: false,
      accountSelectorSearch: '',
      accountSelectorLoading: false,
      allAccounts: [],
      tempSelectedAccounts: [],
      availableSelectedAccounts: [],
      selectedSelectedAccounts: [],
      accountSelectorHeaders: [
        { text: 'Cuenta', value: 'account', sortable: true },
        { text: 'Descripción', value: 'description', sortable: true }
      ],
      supplierSearchTimeout: null,
      customerSearchTimeout: null,
      accountSearchTimeout: null,
      validationRules: {
        required: v => (!!v && (!Array.isArray(v) || v.length > 0)) || 'Campo requerido'
      },
      itemsPerPageText: 'Filas por página:',
      searchFilters: [
        {
          key: 'id',
          label: 'ID',
          type: 'text'
        },
        {
          key: 'account_rule_type',
          label: 'Tipo',
          type: 'select',
          options: [
            { text: 'Por Cuentas', value: 'by_accounts' },
            { text: 'Por Grupo', value: 'by_account_group' }
          ]
        }
      ],
      headers: [
        { text: 'ID', value: 'id', sortable: true, width: '80px' },
        { text: 'Tipo', value: 'account_rule_type', sortable: true, width: '150px' },
        { text: 'Cuentas', value: 'account_rules', sortable: false },
        { text: 'Planes Analíticos', value: 'analytic_plans', sortable: false },
        { text: 'Terceros', value: 'thirds', sortable: false },
        { text: 'Referencias', value: 'references', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end', width: '120px' }
      ]
    };
  },

  computed: {
    dialogTitle() {
      return this.editedIndex === -1 ? 'Nueva Referencia' : 'Editar Referencia';
    },
    analyticPlansOptions() {
      if (!this.analyticPlans) {
        return [];
      }

      const plansArray = this.analyticPlans.information || this.analyticPlans.analytic_plans;

      if (!plansArray || !Array.isArray(plansArray) || plansArray.length === 0) {
        return [];
      }

      return plansArray.map(plan => ({
        code: plan.code,
        name: plan.name,
        levels: plan.levels || []
      }));
    },
    getAnalyticPlansButtonText() {
      const count = this.editedItem.analytic_plans.length;
      if (count === 0) {
        return 'Seleccionar Planes Analíticos';
      }
      return `${count} plan${count > 1 ? 'es' : ''} seleccionado${count > 1 ? 's' : ''}`;
    },
    hasAnySelectedLevels() {
      if (!this.editedItem.analytic_plans || this.editedItem.analytic_plans.length === 0) {
        return false;
      }
      return this.editedItem.analytic_plans.some(plan => this.getSelectedLevelsCount(plan) > 0);
    },
    availableSuppliers() {
      // Filter out suppliers that are already in tempSelectedSuppliers
      const selectedIds = this.tempSelectedSuppliers.map(s => s.id);
      return this.allSuppliers.filter(s => !selectedIds.includes(s.id));
    },
    availableCustomers() {
      // Filter out customers that are already in tempSelectedCustomers
      const selectedIds = this.tempSelectedCustomers.map(c => c.id);
      return this.allCustomers.filter(c => !selectedIds.includes(c.id));
    },
    availableAccounts() {
      // Filter out accounts that are already in tempSelectedAccounts
      const selectedCodes = this.tempSelectedAccounts.map(a => a.account);
      let filtered = this.allAccounts.filter(a => !selectedCodes.includes(a.account));

      // Apply accent-insensitive search if search text is present
      if (this.accountSelectorSearch && this.accountSelectorSearch.trim()) {
        const searchNormalized = this.removeAccents(this.accountSelectorSearch.toLowerCase());
        filtered = filtered.filter(a => {
          const accountNormalized = this.removeAccents((a.account || '').toLowerCase());
          const descriptionNormalized = this.removeAccents((a.description || '').toLowerCase());
          return accountNormalized.includes(searchNormalized) ||
                 descriptionNormalized.includes(searchNormalized);
        });
      }

      return filtered;
    },
    availableThirdsAccounts() {
      // Filter out accounts that are already in tempSelectedThirdsAccounts
      const selectedCodes = this.tempSelectedThirdsAccounts.map(a => a.code);
      return this.allThirdsAccounts.filter(a => !selectedCodes.includes(a.code));
    }
  },

  mounted() {
    this.loadReferences();
    this.fetchAnalyticPlans();
  },

  methods: {
    removeAccents(text) {
      if (!text) return text;
      // Normalize to NFD (decomposed form) and remove combining diacritical marks
      return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
    getDefaultItem() {
      return {
        id: null,
        account_rule_type: 'by_accounts',
        account_rules: [],
        analytic_plans: [],
        thirds: [],
        references: [],
        apply_to_suppliers: false,
        apply_to_customers: false
      };
    },
    async loadReferences() {
      this.loading = true;
      try {
        const company = await this.companyRepository.find_by_id(this.companydbId);
        this.references = company.analytic_plan_references || [];
        console.log('Loaded references:', this.references.length, this.references);

        // Debug: Check if ui_visible is in the loaded data
        if (this.references.length > 0) {
          this.references.forEach((ref, refIdx) => {
            console.log(`Reference ${refIdx}:`, JSON.parse(JSON.stringify(ref)));
            if (ref.analytic_plans && ref.analytic_plans.length > 0) {
              ref.analytic_plans.forEach((plan, planIdx) => {
                console.log(`  Plan ${planIdx} (${plan.name}):`);
                if (plan.levels && plan.levels.length > 0) {
                  plan.levels.forEach((level1, l1idx) => {
                    console.log(`    Level1[${l1idx}] ${level1.code}: ui_visible=${level1.ui_visible}`);
                    if (level1.levels && level1.levels.length > 0) {
                      level1.levels.forEach((level2, l2idx) => {
                        console.log(`      Level2[${l2idx}] ${level2.code}: ui_visible=${level2.ui_visible}`);
                      });
                    }
                  });
                }
              });
            }
          });
        }
      } catch (error) {
        console.error('Error loading references:', error);
        console.error('No se pudieron cargar las referencias');
      } finally {
        this.loading = false;
      }
    },

    refreshList() {
      this.loadReferences();
    },

    openCreateDialog() {
      this.editedIndex = -1;
      this.editedItem = this.getDefaultItem();
      this.originalEditedItem = JSON.parse(JSON.stringify(this.editedItem));
      this.hasUnsavedChanges = false;
      this.selectedSuppliers = [];
      this.selectedCustomers = [];
      this.resetSupplierItems();
      this.resetCustomerItems();
      if (this.$refs.referenceForm) {
        this.$refs.referenceForm.reset();
      }
      this.dialog = true;
    },

    openDeleteDialog() {
      if (this.selectedReferences.length > 0) {
        this.deleteDialog = true;
      }
    },

    deleteReference(item) {
      this.selectedReferences = [item];
      this.openDeleteDialog();
    },

    async editReference(item) {
      this.editedIndex = this.references.indexOf(item);
      // Deep copy the item - use JSON for deep copy of nested analytic plans with levels
      this.editedItem = JSON.parse(JSON.stringify(item));

      // Ensure boolean fields have proper values (not undefined)
      this.editedItem.apply_to_suppliers = this.editedItem.apply_to_suppliers !== undefined ? this.editedItem.apply_to_suppliers : false;
      this.editedItem.apply_to_customers = this.editedItem.apply_to_customers !== undefined ? this.editedItem.apply_to_customers : false;

      // Convert account_rules from strings to objects with descriptions
      // BUT only for "by_accounts" rule type - NOT for "by_account_group"
      if (this.editedItem.account_rules && this.editedItem.account_rules.length > 0) {
        if (this.editedItem.account_rule_type === 'by_accounts') {
          // Fetch descriptions for exact account matching
          const accountCodes = this.editedItem.account_rules.map(rule =>
            typeof rule === 'object' ? rule.account : rule
          );
          this.editedItem.account_rules = await this.fetchAccountDescriptions(accountCodes);
        } else {
          // For group rules, keep as simple strings (prefixes only)
          this.editedItem.account_rules = this.editedItem.account_rules.map(rule =>
            typeof rule === 'object' ? rule.account : rule
          );
        }
      }

      // Split thirds into suppliers and customers
      const thirds = this.editedItem.thirds || [];
      this.selectedSuppliers = thirds.filter(t => t.third_type === 'supplier');
      this.selectedCustomers = thirds.filter(t => t.third_type === 'customer');
      // Populate options with selected items so they display in the autocomplete
      this.supplierOptions = [...this.selectedSuppliers];
      this.customerOptions = [...this.selectedCustomers];

      // Store the original AFTER all transformations are complete
      this.originalEditedItem = JSON.parse(JSON.stringify(this.editedItem));
      this.hasUnsavedChanges = false;

      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.editedIndex = -1;
      this.editedItem = this.getDefaultItem();
      this.originalEditedItem = null;
      this.hasUnsavedChanges = false;
      this.selectedSuppliers = [];
      this.selectedCustomers = [];
      this.resetSupplierItems();
      this.resetCustomerItems();
      this.analyticPlansError = false;
    },

    closeDialogWithCheck() {
      if (this.hasUnsavedChanges) {
        // Show confirmation dialog
        this.unsavedChangesDialog = true;
      } else {
        // No unsaved changes, close directly
        this.closeDialog();
      }
    },

    confirmCloseWithoutSaving() {
      // Close the confirmation dialog
      this.unsavedChangesDialog = false;
      // Close the main dialog
      this.closeDialog();
    },

    normalizeAccountRules(accountRules) {
      // Normalize account rules: convert objects to strings (account codes)
      return accountRules.map(acc => {
        if (typeof acc === 'string') {
          return acc;
        } else if (acc && typeof acc === 'object' && acc.account) {
          return acc.account;
        }
        return null;
      }).filter(acc => acc !== null);
    },

    async saveReference() {
      // Validate form
      if (this.$refs.referenceForm && !this.$refs.referenceForm.validate()) {
        console.error('Por favor completa todos los campos requeridos');
        return;
      }

      // Validate analytic plans
      if (!this.editedItem.analytic_plans || this.editedItem.analytic_plans.length === 0) {
        this.analyticPlansError = true;
        return;
      }
      this.analyticPlansError = false;

      this.saving = true;

      try {
        // Normalize account rules (convert objects to strings if needed)
        const normalizedAccountRules = this.normalizeAccountRules(this.editedItem.account_rules);

        // Get or generate ID
        let newId;
        if (this.editedIndex > -1) {
          // Editing existing reference - preserve the original ID
          newId = this.references[this.editedIndex].id;
        } else {
          // Creating new reference - generate a temporary ID
          newId = this.references.length > 0
            ? Math.max(...this.references.map(r => r.id)) + 1
            : 1;
        }

        // Combine suppliers and customers into thirds array
        // Filter out any invalid items (items without an id)
        const validSuppliers = this.selectedSuppliers.filter(s => s && typeof s === 'object' && s.id);
        const validCustomers = this.selectedCustomers.filter(c => c && typeof c === 'object' && c.id);

        console.log('Selected suppliers:', this.selectedSuppliers);
        console.log('Valid suppliers:', validSuppliers);
        console.log('Selected customers:', this.selectedCustomers);
        console.log('Valid customers:', validCustomers);

        const combinedThirds = [
          ...validSuppliers.map(s => ({ ...s, third_type: 'supplier' })),
          ...validCustomers.map(c => ({ ...c, third_type: 'customer' }))
        ];

        console.log('Analytic plans before saving:', this.editedItem.analytic_plans);
        this.editedItem.analytic_plans.forEach((plan, idx) => {
          console.log(`Plan ${idx} (${plan.name}):`, JSON.parse(JSON.stringify(plan)));
          if (plan.levels && plan.levels.length > 0) {
            plan.levels.forEach((level1, l1idx) => {
              const level1Data = {
                code: level1.code,
                name: level1.name,
                ui_visible: level1.ui_visible,
                hasChildren: level1.levels && level1.levels.length > 0
              };
              console.log(`  Level1[${l1idx}]:`, level1Data);
              if (level1.levels && level1.levels.length > 0) {
                level1.levels.forEach((level2, l2idx) => {
                  const level2Data = {
                    code: level2.code,
                    name: level2.name,
                    ui_visible: level2.ui_visible
                  };
                  console.log(`    Level2[${l2idx}]:`, level2Data);
                });
              }
            });
          }
        });

        // Build the reference object
        const referenceToSave = {
          id: newId,
          account_rule_type: this.editedItem.account_rule_type,
          account_rules: normalizedAccountRules,
          analytic_plans: this.editedItem.analytic_plans || [],
          thirds: combinedThirds,
          references: this.editedItem.references || [],
          apply_to_suppliers: this.editedItem.apply_to_suppliers || false,
          apply_to_customers: this.editedItem.apply_to_customers || false
        };

        console.log('Combined thirds:', combinedThirds);
        console.log('Reference to save:', referenceToSave);

        // Update the references array
        let updatedReferences = [...this.references];
        if (this.editedIndex > -1) {
          // Editing existing reference
          updatedReferences[this.editedIndex] = referenceToSave;
        } else {
          // Creating new reference
          updatedReferences.push(referenceToSave);
        }

        console.log('All references to save:', updatedReferences);

        // Save to backend
        await this.companyRepository.save_analytic_plan_references(
          this.userId,
          this.companydbId,
          updatedReferences
        );

        const isNewReference = this.editedIndex === -1;
        console.log(isNewReference ? 'Referencia creada correctamente' : 'Referencia actualizada correctamente');

        // Reload the references list to get the updated data
        await this.loadReferences();

        // If it was a new reference, update editedIndex and editedItem.id
        // so subsequent saves update instead of creating duplicates
        if (isNewReference) {
          // Find the newly created reference by its ID
          const savedReferenceIndex = this.references.findIndex(ref => ref.id === newId);
          if (savedReferenceIndex !== -1) {
            this.editedIndex = savedReferenceIndex;
            this.editedItem.id = newId;
          }
        }

        // Mark as saved (no unsaved changes)
        this.hasUnsavedChanges = false;
        // Update the original to the current saved state
        this.originalEditedItem = JSON.parse(JSON.stringify(this.editedItem));
      } catch (error) {
        console.error('Error saving reference:', error);
        if (error.response && error.response.data) {
          console.error('Server error details:', error.response.data);
        }
        console.error('No se pudo guardar la referencia');
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete() {
      this.loading = true;

      try {
        // Get IDs of references to delete
        const idsToDelete = this.selectedReferences.map(ref => ref.id);

        // Filter out the references to delete
        const updatedReferences = this.references.filter(ref => !idsToDelete.includes(ref.id));

        // Save to backend
        await this.companyRepository.save_analytic_plan_references(
          this.userId,
          this.companydbId,
          updatedReferences
        );

        console.log(`${this.selectedReferences.length} referencia(s) eliminada(s) correctamente`);

        this.deleteDialog = false;
        this.selectedReferences = [];
        await this.loadReferences();
      } catch (error) {
        console.error('Error deleting references:', error);
        console.error('No se pudieron eliminar las referencias');
      } finally {
        this.loading = false;
      }
    },

    getReferenceTypeLabel(type) {
      const labels = {
        'by_accounts': 'Por Cuentas',
        'by_account_group': 'Por Grupo'
      };
      return labels[type] || type;
    },

    getReferenceTypeColor(type) {
      const colors = {
        'by_accounts': 'green',
        'by_account_group': 'blue'
      };
      return colors[type] || 'grey';
    },

    removeSupplier(supplier) {
      const index = this.selectedSuppliers.findIndex(s => s.id === supplier.id);
      if (index > -1) {
        this.selectedSuppliers.splice(index, 1);
      }
    },

    removeCustomer(customer) {
      const index = this.selectedCustomers.findIndex(c => c.id === customer.id);
      if (index > -1) {
        this.selectedCustomers.splice(index, 1);
      }
    },

    async openSupplierSelectorDialog() {
      // Initialize temp selected suppliers with current selection
      this.tempSelectedSuppliers = JSON.parse(JSON.stringify(this.selectedSuppliers));

      // Clear selections and search
      this.availableSelectedSuppliers = [];
      this.selectedSelectedSuppliers = [];
      this.supplierSelectorSearch = '';

      // Open dialog
      this.supplierSelectorDialog = true;

      // Load initial batch of suppliers (large enough to cover most cases)
      await this.loadAllSuppliers();
    },

    async loadAllSuppliers() {
      try {
        this.supplierLoading = true;

        const response = await this.searchThirdsQuery.execute({
          companyId: this.companydbId,
          userId: this.userId,
          text: this.supplierSelectorSearch || '',
          thirdType: 'supplier',
          limit: 5000,  // Load a large batch to cover most cases
          offset: 0
        });

        this.allSuppliers = response.results;
        this.supplierTotalCount = response.total_count;
      } catch (error) {
        console.error('Error loading all suppliers:', error);
      } finally {
        this.supplierLoading = false;
      }
    },

    addSuppliersToSelected() {
      // Move selected available suppliers to selected
      this.tempSelectedSuppliers.push(...this.availableSelectedSuppliers);
      this.availableSelectedSuppliers = [];
    },

    addAllSuppliersToSelected() {
      // Move all available suppliers to selected
      this.tempSelectedSuppliers.push(...this.availableSuppliers);
    },

    removeSuppliersFromSelected() {
      // Remove selected suppliers from selected list
      const idsToRemove = this.selectedSelectedSuppliers.map(s => s.id);
      this.tempSelectedSuppliers = this.tempSelectedSuppliers.filter(s => !idsToRemove.includes(s.id));
      this.selectedSelectedSuppliers = [];
    },

    removeAllSuppliersFromSelected() {
      // Remove all suppliers from selected
      this.tempSelectedSuppliers = [];
      this.selectedSelectedSuppliers = [];
    },

    applySupplierSelector() {
      // Apply the selection
      this.selectedSuppliers = JSON.parse(JSON.stringify(this.tempSelectedSuppliers));
      this.supplierSelectorDialog = false;
    },

    cancelSupplierSelector() {
      // Cancel and close
      this.supplierSelectorDialog = false;
      this.tempSelectedSuppliers = [];
      this.availableSelectedSuppliers = [];
      this.selectedSelectedSuppliers = [];
    },

    onAvailableSupplierDoubleClick(event, { item }) {
      // Move the double-clicked supplier to selected
      this.tempSelectedSuppliers.push(item);
    },

    onSelectedSupplierDoubleClick(event, { item }) {
      // Remove the double-clicked supplier from selected
      const index = this.tempSelectedSuppliers.findIndex(s => s.id === item.id);
      if (index > -1) {
        this.tempSelectedSuppliers.splice(index, 1);
      }
    },

    // Customer Selector Dialog methods
    async openCustomerSelectorDialog() {
      // Initialize temp selected customers with current selection
      this.tempSelectedCustomers = JSON.parse(JSON.stringify(this.selectedCustomers));

      // Clear selections and search
      this.availableSelectedCustomers = [];
      this.selectedSelectedCustomers = [];
      this.customerSelectorSearch = '';

      // Open dialog
      this.customerSelectorDialog = true;

      // Load initial batch of customers (large enough to cover most cases)
      await this.loadAllCustomers();
    },

    async loadAllCustomers() {
      try {
        this.customerLoading = true;

        const response = await this.searchThirdsQuery.execute({
          companyId: this.companydbId,
          userId: this.userId,
          text: this.customerSelectorSearch || '',
          thirdType: 'customer',
          limit: 5000,  // Load a large batch to cover most cases
          offset: 0
        });

        this.allCustomers = response.results;
        this.customerTotalCount = response.total_count;
      } catch (error) {
        console.error('Error loading all customers:', error);
      } finally {
        this.customerLoading = false;
      }
    },

    addCustomersToSelected() {
      // Move selected available customers to selected
      this.tempSelectedCustomers.push(...this.availableSelectedCustomers);
      this.availableSelectedCustomers = [];
    },

    removeCustomersFromSelected() {
      // Remove selected customers from selected list
      const idsToRemove = this.selectedSelectedCustomers.map(c => c.id);
      this.tempSelectedCustomers = this.tempSelectedCustomers.filter(c => !idsToRemove.includes(c.id));
      this.selectedSelectedCustomers = [];
    },

    applyCustomerSelector() {
      // Apply the selection
      this.selectedCustomers = JSON.parse(JSON.stringify(this.tempSelectedCustomers));
      this.customerSelectorDialog = false;
    },

    cancelCustomerSelector() {
      // Cancel and close
      this.customerSelectorDialog = false;
      this.tempSelectedCustomers = [];
      this.availableSelectedCustomers = [];
      this.selectedSelectedCustomers = [];
    },

    onAvailableCustomerDoubleClick(event, { item }) {
      // Move the double-clicked customer to selected
      this.tempSelectedCustomers.push(item);
    },

    onSelectedCustomerDoubleClick(event, { item }) {
      // Remove the double-clicked customer from selected
      const index = this.tempSelectedCustomers.findIndex(c => c.id === item.id);
      if (index > -1) {
        this.tempSelectedCustomers.splice(index, 1);
      }
    },

    // Account Selector Dialog methods
    async openAccountSelectorDialog() {
      // Initialize temp selected accounts with current selection (convert from editedItem.account_rules)
      this.tempSelectedAccounts = JSON.parse(JSON.stringify(this.editedItem.account_rules || []));

      // Clear other state
      this.availableSelectedAccounts = [];
      this.selectedSelectedAccounts = [];
      this.accountSelectorSearch = '';

      // Open dialog
      this.accountSelectorDialog = true;

      // Always reload accounts to ensure fresh data
      await this.loadAllAccounts();
    },

    async loadAllAccounts() {
      this.accountSelectorLoading = true;
      try {
        const url = `accounts_all/?companydb=${this.companydbId}&limit=5000`;
        const response = await client.get(url);
        const results = response.data.results || response.data;

        this.allAccounts = results.map(acc => ({
          account: acc.account || acc,
          description: acc.description || ''
        }));
      } catch (error) {
        console.error('Error loading all accounts:', error);
        this.allAccounts = [];
      } finally {
        this.accountSelectorLoading = false;
      }
    },

    addAccountsToSelected() {
      // Move selected available accounts to selected
      this.tempSelectedAccounts.push(...this.availableSelectedAccounts);
      this.availableSelectedAccounts = [];
    },

    removeAccountsFromSelected() {
      // Remove selected accounts from selected list
      const codesToRemove = this.selectedSelectedAccounts.map(a => a.account);
      this.tempSelectedAccounts = this.tempSelectedAccounts.filter(a => !codesToRemove.includes(a.account));
      this.selectedSelectedAccounts = [];
    },

    applyAccountSelector() {
      // Apply the selection to editedItem.account_rules
      this.editedItem.account_rules = JSON.parse(JSON.stringify(this.tempSelectedAccounts));
      this.accountSelectorDialog = false;
    },

    cancelAccountSelector() {
      // Cancel and close
      this.accountSelectorDialog = false;
      this.tempSelectedAccounts = [];
      this.allAccounts = [];
      this.availableSelectedAccounts = [];
      this.selectedSelectedAccounts = [];
      this.accountSelectorSearch = '';
    },

    onAvailableAccountDoubleClick(event, { item }) {
      // Move the double-clicked account to selected
      this.tempSelectedAccounts.push(item);
    },

    onSelectedAccountDoubleClick(event, { item }) {
      // Remove the double-clicked account from selected
      const index = this.tempSelectedAccounts.findIndex(a => a.account === item.account);
      if (index > -1) {
        this.tempSelectedAccounts.splice(index, 1);
      }
    },

    async fetchAnalyticPlans() {
      try {
        const response = await client.get('analytic_plans/', {
          params: { companydb_id: this.companydbId }
        });

        const results = response.data.results || response.data;

        if (results && results.length > 0) {
          this.analyticPlans = results[0];
        }
      } catch (error) {
        console.error('Error fetching analytic plans:', error);
      }
    },

    async fetchAccounts(searchText = '') {
      try {
        if (!searchText || searchText.trim() === '') {
          this.accountOptions = [];
          return;
        }

        const url = `accounts_all/?companydb=${this.companydbId}&text__icontains=${searchText}`;
        const response = await client.get(url);
        const results = response.data.results || response.data;

        this.accountOptions = results.map(acc => ({
          account: acc.account || acc,
          description: acc.description || ''
        }));
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    },

    onAccountNativeInput(event) {
      // Disable API search since we're using selector buttons
      return;
    },

    resetAccountItems() {
      this.accountOptions = [];
    },

    removeAccountRule(item) {
      const accountCode = typeof item === 'object' ? item.account : item;
      this.editedItem.account_rules = this.editedItem.account_rules.filter(rule => {
        const ruleCode = typeof rule === 'object' ? rule.account : rule;
        return ruleCode !== accountCode;
      });
    },

    async normalizeAccountRulesInput() {
      // For "by_account_group", keep entries as strings (group codes)
      if (this.editedItem.account_rule_type === 'by_account_group') {
        return;
      }

      // Convert any string entries to objects with descriptions (only for "by_accounts")
      if (!this.editedItem.account_rules || this.editedItem.account_rules.length === 0) {
        return;
      }

      const stringEntries = this.editedItem.account_rules.filter(rule => typeof rule === 'string');
      if (stringEntries.length === 0) {
        return; // All entries are already objects
      }

      // Fetch descriptions for string entries
      const enrichedEntries = await this.fetchAccountDescriptions(stringEntries);

      // Replace string entries with enriched objects
      this.editedItem.account_rules = this.editedItem.account_rules.map(rule => {
        if (typeof rule === 'string') {
          return enrichedEntries.find(e => e.account === rule) || { account: rule, description: '' };
        }
        return rule;
      });
    },

    filterAccountBy(item, queryText) {
      if (!item || !item.account) return false;
      const text = item.account.toLowerCase() + ' - ' + (item.description || '').toLowerCase();
      const searchText = queryText.toLowerCase();
      return text.indexOf(searchText) > -1;
    },

    async fetchThirdParties(searchText = '') {
      try {
        console.log('[fetchThirdParties] Called with:', { searchText, selectedThirdType: this.selectedThirdType });

        if (!searchText || searchText.trim() === '') {
          console.log('[fetchThirdParties] Empty search text, clearing options');
          this.thirdPartyOptions = [];
          return;
        }

        if (searchText.trim().length < 3) {
          console.log('[fetchThirdParties] Search text too short, need at least 3 characters');
          return;
        }

        if (!this.selectedThirdType) {
          console.log('[fetchThirdParties] No third type selected, clearing options');
          this.thirdPartyOptions = [];
          return;
        }

        if (searchText.trim() === this.thirdPartyLastSearch) {
          console.log('[fetchThirdParties] Same search as last time, skipping');
          return;
        }

        if (this.thirdPartyAbortController) {
          this.thirdPartyAbortController.abort();
        }

        this.thirdPartyLastSearch = searchText.trim();
        this.thirdPartyLoading = true;
        this.thirdPartyAbortController = new AbortController();

        console.log('[fetchThirdParties] Executing search query:', {
          companyId: this.companydbId,
          userId: this.userId,
          text: searchText.trim(),
          thirdType: this.selectedThirdType,
          limit: 10
        });

        // Search only the selected third type
        const results = await this.searchThirdsQuery.execute({
          companyId: this.companydbId,
          userId: this.userId,
          text: searchText.trim(),
          thirdType: this.selectedThirdType,
          limit: 10
        });

        console.log('[fetchThirdParties] Search results:', results);
        this.thirdPartyOptions = results;

        this.thirdPartyLoading = false;
        this.thirdPartyAbortController = null;
      } catch (error) {
        if (error.name === 'AbortError' || error.message === 'canceled') {
          console.log('[fetchThirdParties] Request was cancelled');
        } else {
          console.error('[fetchThirdParties] Error fetching third parties:', error);
          const errorDetails = (error.response && error.response.data) ? error.response.data : error.message;
          console.error('[fetchThirdParties] Error details:', errorDetails);
        }
        this.thirdPartyLoading = false;
        this.thirdPartyAbortController = null;
      }
    },

    onThirdPartyNativeInput(event) {
      const searchText = event.target.value;

      if (this.thirdPartySearchDebounce) {
        clearTimeout(this.thirdPartySearchDebounce);
      }

      this.thirdPartySearchDebounce = setTimeout(() => {
        this.fetchThirdParties(searchText);
      }, 500);
    },

    resetThirdPartyItems() {
      if (this.thirdPartyAbortController) {
        this.thirdPartyAbortController.abort();
        this.thirdPartyAbortController = null;
      }

      if (this.thirdPartySearchDebounce) {
        clearTimeout(this.thirdPartySearchDebounce);
        this.thirdPartySearchDebounce = null;
      }

      this.thirdPartyOptions = [];
      this.thirdPartyLastSearch = '';
    },

    filterThirdPartyBy(item, queryText) {
      if (!item || !item.name) return false;
      const text = (item.account || '').toLowerCase() + ' - ' + item.name.toLowerCase();
      const searchText = queryText.toLowerCase();
      return text.indexOf(searchText) > -1;
    },

    // Supplier search methods
    onSupplierInput(event) {
      const searchText = event.target.value;

      if (this.supplierSearchDebounce) {
        clearTimeout(this.supplierSearchDebounce);
      }

      this.supplierSearchDebounce = setTimeout(() => {
        this.fetchSuppliers(searchText);
      }, 500);
    },

    async fetchSuppliers(searchText = '') {
      try {
        if (!searchText || searchText.trim() === '' || searchText.trim().length < 1) {
          this.supplierOptions = [];
          this.supplierLastSearch = '';
          return;
        }

        if (this.supplierAbortController) {
          this.supplierAbortController.abort();
        }

        this.supplierLastSearch = searchText.trim();
        this.supplierLoading = true;
        this.supplierAbortController = new AbortController();

        const results = await this.searchThirdsQuery.execute({
          companyId: this.companydbId,
          userId: this.userId,
          text: searchText.trim(),
          thirdType: 'supplier',
          limit: 10
        });

        this.supplierOptions = results;
        this.supplierLoading = false;
        this.supplierAbortController = null;
      } catch (error) {
        if (error.name === 'AbortError' || error.message === 'canceled') {
          // Request was cancelled
        } else {
          console.error('Error fetching suppliers:', error);
        }
        this.supplierLoading = false;
        this.supplierAbortController = null;
      }
    },

    resetSupplierItems() {
      this.supplierOptions = [];
      this.supplierLastSearch = '';
    },

    // Customer search methods
    onCustomerInput(event) {
      const searchText = event.target.value;

      if (this.customerSearchDebounce) {
        clearTimeout(this.customerSearchDebounce);
      }

      this.customerSearchDebounce = setTimeout(() => {
        this.fetchCustomers(searchText);
      }, 500);
    },

    async fetchCustomers(searchText = '') {
      try {
        if (!searchText || searchText.trim() === '' || searchText.trim().length < 1) {
          this.customerOptions = [];
          this.customerLastSearch = '';
          return;
        }

        if (this.customerAbortController) {
          this.customerAbortController.abort();
        }

        this.customerLastSearch = searchText.trim();
        this.customerLoading = true;
        this.customerAbortController = new AbortController();

        const results = await this.searchThirdsQuery.execute({
          companyId: this.companydbId,
          userId: this.userId,
          text: searchText.trim(),
          thirdType: 'customer',
          limit: 10
        });

        this.customerOptions = results;
        this.customerLoading = false;
        this.customerAbortController = null;
      } catch (error) {
        if (error.name === 'AbortError' || error.message === 'canceled') {
          // Request was cancelled
        } else {
          console.error('Error fetching customers:', error);
        }
        this.customerLoading = false;
        this.customerAbortController = null;
      }
    },

    resetCustomerItems() {
      this.customerOptions = [];
      this.customerLastSearch = '';
    },

    // Analytic Plans Dialog methods
    openAnalyticPlansDialog() {
      this.analyticPlansDialog = true;
    },

    closeAnalyticPlansDialog() {
      this.analyticPlansDialog = false;
    },

    // Reference editing methods
    openEditReferenceDialog(item, index) {
      this.editingReferenceIndex = index;
      this.editingReferenceText = item;
      this.editReferenceDialog = true;
    },

    saveEditedReference() {
      if (this.editingReferenceText && this.editingReferenceText.trim() !== '') {
        // Replace the reference at the stored index
        this.$set(this.editedItem.references, this.editingReferenceIndex, this.editingReferenceText.trim());
      }
      this.editReferenceDialog = false;
      this.editingReferenceIndex = null;
      this.editingReferenceText = '';
    },

    // Account Rule editing methods (for by_account_group type)
    openEditAccountRuleDialog(item, index) {
      this.editingAccountRuleIndex = index;
      // Extract the account code from the item
      this.editingAccountRuleText = typeof item === 'object' ? item.account : item;
      this.editAccountRuleDialog = true;
    },

    saveEditedAccountRule() {
      if (this.editingAccountRuleText && this.editingAccountRuleText.trim() !== '') {
        // Replace the account rule at the stored index
        this.$set(this.editedItem.account_rules, this.editingAccountRuleIndex, this.editingAccountRuleText.trim());
      }
      this.editAccountRuleDialog = false;
      this.editingAccountRuleIndex = null;
      this.editingAccountRuleText = '';
    },

    async openThirdsAccountsDialog() {
      // Collect all unique accounts from selected thirds
      const accountsSet = new Set();

      // From suppliers
      this.selectedSuppliers.forEach(supplier => {
        if (supplier.account) {
          accountsSet.add(supplier.account);
        }
        if (supplier.expens_account) {
          accountsSet.add(supplier.expens_account);
        }
      });

      // From customers
      this.selectedCustomers.forEach(customer => {
        if (customer.account) {
          accountsSet.add(customer.account);
        }
        if (customer.income_account) {
          accountsSet.add(customer.income_account);
        }
      });

      // Get all account codes from selected thirds
      const accountCodes = Array.from(accountsSet);

      // Fetch actual account descriptions from API for all accounts
      const accountsWithDescriptions = await this.fetchAccountDescriptions(accountCodes);

      // Store ALL thirds accounts (computed property will filter for display in left table)
      this.allThirdsAccounts = accountsWithDescriptions
        .map(acc => ({
          code: acc.account,
          description: acc.description || ''
        }))
        .sort((a, b) => a.code.localeCompare(b.code));

      // Get existing account rules (normalize to strings)
      const existingRules = (this.editedItem.account_rules || []).map(rule =>
        typeof rule === 'object' ? rule.account : String(rule)
      );

      // Set already selected accounts (right table) - only those that exist in account_rules
      this.tempSelectedThirdsAccounts = this.allThirdsAccounts
        .filter(acc => existingRules.includes(acc.code))
        .map(acc => ({ ...acc })); // Clone to avoid mutation issues

      // Clear selections
      this.availableSelectedThirdsAccounts = [];
      this.selectedSelectedThirdsAccounts = [];
      this.thirdsAccountsSearch = '';

      // Open dialog
      this.thirdsAccountsDialog = true;
    },

    async applyThirdsAccounts() {
      // Get all account codes that were shown in this dialog (from thirds)
      const thirdsAccountCodes = [
        ...this.allThirdsAccounts.map(acc => acc.code),
        ...this.tempSelectedThirdsAccounts.map(acc => acc.code)
      ];

      // Keep existing account_rules that are NOT from thirds (preserve manual selections)
      const existingRules = this.editedItem.account_rules || [];
      const nonThirdsAccounts = existingRules.filter(rule => {
        const code = typeof rule === 'object' ? rule.account : rule;
        return !thirdsAccountCodes.includes(code);
      });

      // Convert temp selected accounts to proper format
      const selectedThirdsAccounts = this.tempSelectedThirdsAccounts.map(acc => ({
        account: acc.code,
        description: acc.description
      }));

      // Combine non-thirds accounts with selected thirds accounts
      this.editedItem.account_rules = [...nonThirdsAccounts, ...selectedThirdsAccounts];

      // Close dialog
      this.thirdsAccountsDialog = false;
    },

    cancelThirdsAccountsSelector() {
      // Close dialog and clear temp state
      this.thirdsAccountsDialog = false;
      this.tempSelectedThirdsAccounts = [];
      this.availableSelectedThirdsAccounts = [];
      this.selectedSelectedThirdsAccounts = [];
      this.thirdsAccountsSearch = '';
    },

    addThirdsAccountsToSelected() {
      // Move selected available accounts to selected
      this.tempSelectedThirdsAccounts.push(...this.availableSelectedThirdsAccounts);
      this.availableSelectedThirdsAccounts = [];
    },

    removeThirdsAccountsFromSelected() {
      // Remove selected accounts from selected list
      const codesToRemove = this.selectedSelectedThirdsAccounts.map(acc => acc.code);
      this.tempSelectedThirdsAccounts = this.tempSelectedThirdsAccounts.filter(acc => !codesToRemove.includes(acc.code));
      this.selectedSelectedThirdsAccounts = [];
    },

    onAvailableThirdsAccountDoubleClick(event, { item }) {
      // Move the double-clicked account to selected
      this.tempSelectedThirdsAccounts.push(item);
    },

    onSelectedThirdsAccountDoubleClick(event, { item }) {
      // Remove the double-clicked account from selected
      const index = this.tempSelectedThirdsAccounts.findIndex(acc => acc.code === item.code);
      if (index > -1) {
        this.tempSelectedThirdsAccounts.splice(index, 1);
      }
    },

    async fetchAccountDescriptions(accountCodes) {
      try {
        // Fetch account details for all codes
        const promises = accountCodes.map(async (code) => {
          try {
            const url = `accounts_all/?companydb=${this.companydbId}&text__icontains=${code}`;
            const response = await client.get(url);
            const results = response.data.results || response.data;

            // Find exact match (not just contains)
            let account;
            if (Array.isArray(results)) {
              account = results.find(acc => acc.account === code) || results[0];
            } else {
              account = results;
            }

            return {
              account: code,
              description: account && account.description ? account.description : ''
            };
          } catch (error) {
            // If fetch fails, return just the code
            return {
              account: code,
              description: ''
            };
          }
        });

        return await Promise.all(promises);
      } catch (error) {
        console.error('Error fetching account descriptions:', error);
        // Fallback: return codes without descriptions
        return accountCodes.map(code => ({ account: code, description: '' }));
      }
    },

    isPlanSelected(plan) {
      return this.editedItem.analytic_plans.some(p => p.code === plan.code);
    },

    togglePlanSelection(plan) {
      const index = this.editedItem.analytic_plans.findIndex(p => p.code === plan.code);
      if (index > -1) {
        // Remove plan
        this.editedItem.analytic_plans.splice(index, 1);
      } else {
        // Add plan with deep copy of levels
        const planCopy = JSON.parse(JSON.stringify(plan));
        this.editedItem.analytic_plans.push(planCopy);
        // Clear error when a plan is added
        this.analyticPlansError = false;
      }
    },

    removeAnalyticPlan(index) {
      this.editedItem.analytic_plans.splice(index, 1);
    },

    openLevelSelector(plan) {
      // Find the plan in editedItem.analytic_plans (not from options)
      const planIndex = this.editedItem.analytic_plans.findIndex(p => p.code === plan.code);
      if (planIndex > -1) {
        this.currentPlanForLevelSelection = this.editedItem.analytic_plans[planIndex];
        this.levelSelectorDialog = true;
      }
    },

    onLevelSelectionApply() {
      // The AnalyticLevelSelectorPopup modifies the plan directly (by reference)
      // So we don't need to do anything here, just close the dialog
      this.levelSelectorDialog = false;
      this.currentPlanForLevelSelection = null;
    },

    getSelectedLevelsCount(plan) {
      let count = 0;
      if (!plan || !plan.levels) return count;

      plan.levels.forEach(level1 => {
        if (level1.levels && level1.levels.length > 0) {
          // Count selected level 2 items
          level1.levels.forEach(level2 => {
            if (level2.ui_visible === true) {
              count++;
            }
          });
        } else {
          // Count selected level 1 items (when no children)
          if (level1.ui_visible === true) {
            count++;
          }
        }
      });

      return count;
    },

    getPlanSelectedLevelsTree(plan) {
      // Build a tree of only selected levels for this plan
      const tree = [];

      if (!plan || !plan.levels || !Array.isArray(plan.levels)) {
        return tree;
      }

      plan.levels.forEach(level1 => {
        const node = {
          code: level1.code,
          name: level1.name || level1.code,
          children: [],
          isSelected: false
        };

        // Check if this Level 1 has children
        const hasChildren = level1.levels && Array.isArray(level1.levels) && level1.levels.length > 0;

        if (hasChildren) {
          // Get selected Level 2 children
          level1.levels.forEach(level2 => {
            if (level2.ui_visible === true) {
              node.children.push({
                code: level2.code,
                name: level2.name || level2.code
              });
            }
          });

          // Only add to tree if has selected children
          if (node.children.length > 0) {
            tree.push(node);
          }
        } else {
          // No children: check if the Level 1 itself is selected
          if (level1.ui_visible === true) {
            node.isSelected = true;
            tree.push(node);
          }
        }
      });

      return tree;
    },

    checkForChanges() {
      // Build the current state including suppliers and customers
      const currentThirds = [
        ...this.selectedSuppliers.filter(s => s && typeof s === 'object' && s.id).map(s => ({ ...s, third_type: 'supplier' })),
        ...this.selectedCustomers.filter(c => c && typeof c === 'object' && c.id).map(c => ({ ...c, third_type: 'customer' }))
      ];

      const currentState = {
        ...this.editedItem,
        thirds: currentThirds
      };

      const originalState = {
        ...this.originalEditedItem,
        thirds: this.originalEditedItem.thirds || []
      };

      this.hasUnsavedChanges = JSON.stringify(currentState) !== JSON.stringify(originalState);
    }
  },

  watch: {
    selectedThirdType(newValue, oldValue) {
      console.log('[watch] selectedThirdType changed:', { oldValue, newValue });
      // Clear thirds selection when type changes
      if (oldValue !== null && newValue !== oldValue) {
        this.editedItem.thirds = [];
        this.thirdPartyOptions = [];
        this.thirdPartyLastSearch = '';
      }
    },
    editedItem: {
      handler() {
        // Check if there are changes compared to original
        if (this.originalEditedItem && this.dialog) {
          const current = JSON.stringify(this.editedItem);
          const original = JSON.stringify(this.originalEditedItem);
          this.hasUnsavedChanges = current !== original;
        }
      },
      deep: true
    },
    selectedSuppliers: {
      handler() {
        // Trigger change detection when suppliers change
        if (this.originalEditedItem && this.dialog) {
          this.checkForChanges();
        }
      },
      deep: true
    },
    selectedCustomers: {
      handler() {
        // Trigger change detection when customers change
        if (this.originalEditedItem && this.dialog) {
          this.checkForChanges();
        }
      },
      deep: true
    },
    supplierSelectorSearch(newValue) {
      // Debounce search to avoid too many API calls
      if (this.supplierSearchTimeout) {
        clearTimeout(this.supplierSearchTimeout);
      }
      this.supplierSearchTimeout = setTimeout(() => {
        if (this.supplierSelectorDialog) {
          this.loadAllSuppliers();
        }
      }, 300);
    },
    customerSelectorSearch(newValue) {
      // Debounce search to avoid too many API calls
      if (this.customerSearchTimeout) {
        clearTimeout(this.customerSearchTimeout);
      }
      this.customerSearchTimeout = setTimeout(() => {
        if (this.customerSelectorDialog) {
          this.loadAllCustomers();
        }
      }, 300);
    },
    accountSelectorSearch(newValue) {
      // Debounce search to avoid too many API calls
      if (this.accountSearchTimeout) {
        clearTimeout(this.accountSearchTimeout);
      }
      this.accountSearchTimeout = setTimeout(() => {
        if (this.accountSelectorDialog) {
          this.loadAllAccounts();
        }
      }, 300);
    }
  }
};
</script>

<style scoped>
.analytic-plan-rules-manager {
  padding: 0;
}

/* Table header styling to match original version */
::v-deep .v-data-table-header > tr > th{
    background-color: var(--v-primaryLight-base) !important;
}
::v-deep .v-data-table-header > tr > th > span,
::v-deep .v-data-table-header > tr > th >  div > div > div > label > span{
    color: var(--v-primary-base);
    font-size: 0.875rem;
}

/* Table body row styling */
::v-deep .v-data-table tbody tr td {
    font-size: 0.9375rem;
}

                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          /* Table chip styling */
::v-deep .v-data-table tbody tr td .v-chip {
    font-size: 0.8125rem;
}

/* Sticky header for dialog */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

/* Scrollable content area */
.scrollable-content {
  max-height: calc(90vh - 80px);
  overflow-y: auto;
}

/* Selected levels tree structure */
.selected-levels-tree {
  background-color: rgba(33, 150, 243, 0.05);
  border-left: 3px solid #2196F3;
  border-radius: 4px;
  padding: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.plan-tree-item {
  line-height: 1.6;
}

.plan-name {
  display: flex;
  align-items: center;
  color: #1565C0;
  font-size: 0.875rem;
}

.level1-parent {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  margin-top: 4px;
  margin-bottom: 2px;
}

.level2-child {
  display: flex;
  align-items: center;
  color: #424242;
  margin-top: 2px;
}

.level1-standalone {
  display: flex;
  align-items: center;
  color: #424242;
  margin-top: 2px;
}

/* Scrollbar styling */
.selected-levels-tree::-webkit-scrollbar {
  width: 6px;
}

.selected-levels-tree::-webkit-scrollbar-track {
  background: rgba(33, 150, 243, 0.1);
  border-radius: 3px;
}

.selected-levels-tree::-webkit-scrollbar-thumb {
  background: rgba(33, 150, 243, 0.3);
  border-radius: 3px;
}

.selected-levels-tree::-webkit-scrollbar-thumb:hover {
  background: rgba(33, 150, 243, 0.5);
}
</style>
